import React, { useState } from 'react';
import { Loader2, ChevronDown, AlertTriangle, Plus, Unplug, RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { redes, errorMsg } from './redes.api';
import { RED } from './util';

function Avatar({ c, red }) {
  const R = RED[red];
  if (c.foto) return <img src={c.foto} alt="" className="w-9 h-9 rounded-full object-cover border border-gray-200" />;
  return <span className={`w-9 h-9 rounded-full flex items-center justify-center text-white ${R.fondo}`}><R.Icono size={16} /></span>;
}

function Tarjeta({ info, customerId, onCambio }) {
  const R = RED[info.red];
  const [ayuda, setAyuda] = useState(false);
  const [yendo, setYendo] = useState(false);
  const conectadas = info.cuentas || [];

  const conectar = async () => {
    setYendo(true);
    try {
      const volver = window.location.href.split('#')[0];
      const { url } = await redes.conectar(info.red, customerId, volver);
      window.location.assign(url);
    } catch (e) {
      toast.error(errorMsg(e));
      setYendo(false);
    }
  };
  const desconectar = async (c) => {
    if (!window.confirm(`¿Desconectar ${c.nombre}? Lo que tenías programado para esa cuenta se cancela.`)) return;
    try {
      await redes.desconectar(c.id);
      toast.success('Cuenta desconectada');
      onCambio();
    } catch (e) {
      toast.error(errorMsg(e));
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0 ${R.fondo}`}><R.Icono size={20} /></span>
        <div className="min-w-0">
          <div className="font-semibold text-gray-900">{R.nombre}</div>
          <div className="text-xs text-gray-500">{conectadas.length ? `${conectadas.length} cuenta${conectadas.length > 1 ? 's' : ''} conectada${conectadas.length > 1 ? 's' : ''}` : info.disponible ? 'Sin conectar' : 'Muy pronto'}</div>
        </div>
        {conectadas.length > 0 && <CheckCircle2 size={18} className="text-emerald-500 ml-auto shrink-0" />}
      </div>


      {conectadas.length > 0 && (
        <div className="space-y-2">
          {conectadas.map((c) => (
            <div key={c.id} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
              <div className="flex items-center gap-2">
                <Avatar c={c} red={info.red} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-800 truncate">{c.nombre}</div>
                  <div className="text-[11px] text-gray-500">{{ ig_profesional: 'Cuenta profesional', fb_pagina: 'Página', li_perfil: 'Perfil personal', li_pagina: 'Página de empresa' }[c.tipo]}</div>
                </div>
                <button type="button" onClick={() => desconectar(c)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-white" title="Desconectar" aria-label={`Desconectar ${c.nombre}`}><Unplug size={15} /></button>
              </div>
              {c.estado !== 'ok' && (
                <div className={`mt-2 text-xs rounded-lg px-2.5 py-2 flex gap-2 ${c.estado === 'reconectar' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`}>
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>{c.aviso || 'Volvé a conectar la cuenta.'}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!conectadas.length && <p className="text-sm text-gray-600">{R.queEs}</p>}

      <button type="button" onClick={() => setAyuda(!ayuda)} className="text-xs font-medium text-blue-700 inline-flex items-center gap-1 self-start" aria-expanded={ayuda}>
        ¿Qué necesito? <ChevronDown size={14} className={`transition ${ayuda ? 'rotate-180' : ''}`} />
      </button>
      {ayuda && (
        <ul className="text-xs text-gray-600 space-y-1.5 list-disc pl-4 -mt-1">
          {R.requisitos.map((t) => <li key={t}>{t}</li>)}
          <li>Nunca vemos ni guardamos tu contraseña: la red te pide permiso y nos da un acceso que podés quitar cuando quieras.</li>
        </ul>
      )}

      <div className="mt-auto">
        {info.disponible ? (
          <button type="button" onClick={conectar} disabled={yendo} className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-60 ${conectadas.length ? 'border border-gray-300 text-gray-800 hover:bg-gray-50' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
            {yendo ? <Loader2 size={16} className="animate-spin" /> : conectadas.some((c) => c.estado !== 'ok') ? <RefreshCw size={16} /> : <Plus size={16} />}
            {conectadas.some((c) => c.estado !== 'ok') ? 'Volver a conectar' : conectadas.length ? `Conectar otra ${info.red === 'facebook' ? 'página' : 'cuenta'}` : `Conectar ${R.nombre}`}
          </button>
        ) : (
          <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">Estamos terminando la habilitación con {R.nombre}. Te avisamos cuando esté lista.</div>
        )}
      </div>
    </div>
  );
}

export default function Cuentas({ estado, customerId, onCambio }) {
  if (!estado) return <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{[0, 1, 2].map((i) => <div key={i} className="h-44 bg-white border border-gray-200 rounded-2xl animate-pulse" />)}</div>;
  const n = estado.redes.length;
  return (
    <div className={`grid grid-cols-1 gap-3 ${n >= 3 ? 'md:grid-cols-3' : n === 2 ? 'md:grid-cols-2' : 'md:max-w-md'}`}>
      {estado.redes.map((info) => <Tarjeta key={info.red} info={info} customerId={customerId} onCambio={onCambio} />)}
    </div>
  );
}
